import { useContext } from "react";

import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { CareElementGridHelper } from "@/Flowsheets/Helpers/CareElementGridHelper";
import { CareComponentConfigurationContext } from "@/Flowsheets/Models/CareComponentConfigurationContext";
import { CareElementFrequencyType } from "@/Flowsheets/Models/CareElementFrequencyType";
import { CareElementOperatorType } from "@/Flowsheets/Models/CareElementOperatorType";
import { FrequencyCareElementConfig } from "@/Flowsheets/Models/CareElements/FrequencyCareElementConfig";
import { LabResultsCareElementConfig } from "@/Flowsheets/Models/CareElements/LabResultsCareElementConfig";
import { ReviewedCareElementConfig } from "@/Flowsheets/Models/CareElements/ReviewedCareElementConfig";
import { VitalSignsCareElementConfig } from "@/Flowsheets/Models/CareElements/VitalSignsCareElementConfig";

interface DropdownInputProps<T> {
    enumType: T;
    value?: T[keyof T];
    onChange: (value: T[keyof T]) => void;
    getLabel: (key: T[keyof T]) => string;
    dataCy?: string;
    isInvalid?: boolean;
}

type EnumLike = Record<string | number, string | number>;

function EnumDropdown<T extends EnumLike>({
    enumType,
    value,
    onChange,
    getLabel,
    dataCy,
    isInvalid
}: Readonly<DropdownInputProps<T>>): JSX.Element {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        onChange(e.target.value as T[keyof T]);
    };

    const options = Object.entries(enumType)
        .filter(([key]) => isNaN(Number(key)))
        .map(([key, val]) => ({
            key,
            value: val,
            label: getLabel ? getLabel(val as T[keyof T]) : key.replace(/_/g, " ")
        }));

    return (
        <select data-cy={dataCy} value={value} onChange={handleChange} className={`form-control myle-control${isInvalid ? " invalid" : ""}`}>
            {options.map(option => (
                <option key={option.key} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

function FrequencyTypeDropdown({
    element,
    dataCy
}: Readonly<{
    element: LabResultsCareElementConfig | VitalSignsCareElementConfig | FrequencyCareElementConfig;
    dataCy?: string;
}>): JSX.Element {
    const { getText } = useTranslation();
    const { onEdit } = useContext(CareComponentConfigurationContext);

    function handleFrequencyChange(value: CareElementFrequencyType, element: LabResultsCareElementConfig | VitalSignsCareElementConfig | FrequencyCareElementConfig): void {
        const updatedElement = { ...element, Frequency: value };
        onEdit(element.Name, updatedElement);
    }

    return (
        <EnumDropdown
            enumType={CareElementFrequencyType}
            onChange={value => handleFrequencyChange(value, element)}
            value={element.Frequency}
            dataCy={dataCy}
            getLabel={key => CareElementGridHelper.handleFrequencyLabel(key, getText)}
        />
    );
}

function BooleanDropdown({
    element,
    dataCy
}: Readonly<{
    element: ReviewedCareElementConfig;
    dataCy?: string;
}>): JSX.Element {
    const { getText } = useTranslation();
    const { onEdit } = useContext(CareComponentConfigurationContext);

    function handleBooleanChange(value: string, element: ReviewedCareElementConfig): void {
        let booleanValue: boolean | null;
        if (value === "true") {
            booleanValue = true;
        }
        else if (value === "false") {
            booleanValue = false;
        }
        else {
            booleanValue = null;
        }

        const updatedElement = { ...element, Target: booleanValue };
        onEdit(element.Name, updatedElement);
    }

    let valueToRender = "";
    if (element.Target === true) {
        valueToRender = "true";
    }
    else if (element.Target === false) {
        valueToRender = "false";
    }

    return (
        <SelectKVPControl
            dataCy={dataCy}
            Value={valueToRender}
            AvailableValues={[
                new KeyValuePair("", ""),
                new KeyValuePair("true", getText("YesLabel")),
                new KeyValuePair("false", getText("NoLabel"))
            ]}
            Callback={key => handleBooleanChange(key, element)}
        />
    );
}

function filterOperatorTypes(elementName: string): any {
    let operatorTypeEnum = CareElementOperatorType;
    const elementsNameToExclude = [
        FlowsheetConstants.VitalSigns.BloodPressureCareElementName.toString(),
        FlowsheetConstants.DiabetesBasic.GlucoseMonitoring.NumberOfHypoglycemicEpisodesCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.DyspneaCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.CoughCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.WheezeCareElementName,
        FlowsheetConstants.AsthmaBasic.SymptomsOfAsthma.ChestTightnessCareElementName,
        FlowsheetConstants.AsthmaBasic.Monitoring.RelieverUseCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.FatiguePerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.DizzinessOrSyncopePerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.DyspneaOnExertionPerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.DyspneaAtRestPerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.OrthopneaPerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PhysicalExamination.ParoxysmalNocturnalDyspneaPerWeekCareElementName,
        FlowsheetConstants.HeartFailureBasic.PatientSelfManagement.NumberOfERVisitsSinceLastAssessmentCareElementName
    ];

    if (elementsNameToExclude.includes(elementName)) {
        operatorTypeEnum = Object.fromEntries(
            Object.entries(CareElementOperatorType).filter(([, value]) => value !== CareElementOperatorType.Between)
        ) as typeof CareElementOperatorType;
    }

    return operatorTypeEnum;
}

function OperatorTypeDropdown({
    element,
    dataCy
}: Readonly<{
    element: LabResultsCareElementConfig | VitalSignsCareElementConfig | ReviewedCareElementConfig;
    dataCy?: string;
}>): JSX.Element {
    const { getText } = useTranslation();
    const { onEdit, isCareElementInvalid } = useContext(CareComponentConfigurationContext);

    function handleOperatorChange(value: CareElementOperatorType, element: LabResultsCareElementConfig | VitalSignsCareElementConfig | ReviewedCareElementConfig): void {
        const updatedElement = { ...element, Operator: value };
        onEdit(element.Name, updatedElement);
    }

    return (
        <EnumDropdown
            enumType={filterOperatorTypes(element.Name)}
            onChange={value => handleOperatorChange(value, element)}
            value={element.Operator}
            dataCy={dataCy}
            getLabel={key => CareElementGridHelper.mapOperator(key, getText)}
            isInvalid={isCareElementInvalid?.(element.Name)}
        />
    );
}

export { BooleanDropdown, FrequencyTypeDropdown, OperatorTypeDropdown };
